import React, { useState, useEffect, useCallback } from 'react';
import Modal from '@westwing/ui-kit/Modal';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import styled from 'styled-components';
import MagicLinkForm from '../MagicLinkForm';
import MagicLinkConfirmation from '../MagicLinkSent';
import { StyledPrimaryLinkButton } from '../loginAndRegistration.style';
import EmailNotRecieved from '../EmailNotRecieved/EmailNotRecieved';
import ResetPasswordSent from '../ResetPasswordSent';
import { PasswordResetFormValues } from 'Interfaces/LoginRegisterFormProps';
import { noop } from 'SharedHelpers/callback';
import useApi from 'Hooks/useApi';

const ForgotPasswordWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-top: 25px;
    cursor: pointer;
`;

const ForgotPasswordLabel = styled(StyledPrimaryLinkButton)`
    cursor: pointer;
`;

export const StyledModalBody = styled(Modal.Body)<{ isLoading?: boolean }>`
    background-color: ${props => props.theme.colors.white};
    width: 560px;
    padding: 24px 30px 34px;
`;

const ContainerViews = styled('div')<{ isLoading?: boolean }>`
    opacity: ${({ isLoading }) => (isLoading ? 0.6 : 1)};
    ${forDesktop`
        padding: 24px 30px;
    `}
`;

enum ForgotPasswordActionType {
    LOGIN_LINK = 'LOGIN_LINK',
    RESET_PASSWORD = 'RESET_PASSWORD',
}

export const MAGIC_LINK_FORM_VIEW = 'MAGIC_LINK_FORM';
export const MAGIC_LINK_SENT_VIEW = 'MAGIC_LINK_SENT';
export const RESET_PASSWORD_SENT_VIEW = 'RESET_PASSWORD_SENT';

export const modalViewMap = {
    [MAGIC_LINK_FORM_VIEW]: {
        key: MAGIC_LINK_FORM_VIEW,
        next: (forgotPasswordActionType: ForgotPasswordActionType): string => {
            switch (forgotPasswordActionType) {
                case ForgotPasswordActionType.LOGIN_LINK:
                    return MAGIC_LINK_SENT_VIEW;
                case ForgotPasswordActionType.RESET_PASSWORD:
                    return RESET_PASSWORD_SENT_VIEW;
                default:
                    return '';
            }
        },
    },
    [MAGIC_LINK_SENT_VIEW]: {
        key: MAGIC_LINK_SENT_VIEW,
    },
    [RESET_PASSWORD_SENT_VIEW]: {
        key: RESET_PASSWORD_SENT_VIEW,
    },
};

export type BellaForgotPasswordProps = {
    forgotPasswordLabel: string;
    email?: string;
};

const BellaForgotPassword = ({ forgotPasswordLabel, email }: BellaForgotPasswordProps) => {
    const { resetPassword, sendLoginLink } = useApi();
    const [isLoading, setLoaderStatus] = useState<boolean>(false);
    const [forgotPasswordOverlay, setforgotPasswordOverlayVisibility] = useState<boolean>(false);
    const [actionType, setActionType] = useState<ForgotPasswordActionType | ''>('');
    const [formValues, setFormValues] = useState<PasswordResetFormValues>({ email: '' });
    const [currentView, setCurrentView] = useState(modalViewMap[MAGIC_LINK_FORM_VIEW].key);
    const [customerSupportView, setCustomerSupportView] = useState<boolean>(false);

    const triggerForgotPasswordOverlay = () => setforgotPasswordOverlayVisibility(true);

    const resetState = (closeModal?: boolean) => {
        setActionType('');
        setFormValues({ email: '' });
        setCurrentView(modalViewMap[MAGIC_LINK_FORM_VIEW].key);
        setCustomerSupportView(false);
        if (closeModal) {
            setforgotPasswordOverlayVisibility(false);
        }
    };

    const apiHandlers = useCallback(() => {
        const forgotPasswordTypeApiMap = {
            [ForgotPasswordActionType.RESET_PASSWORD]: resetPassword,
            [ForgotPasswordActionType.LOGIN_LINK]: sendLoginLink,
        };

        const onApiSuccess = () => {
            const nextModalView = modalViewMap[MAGIC_LINK_FORM_VIEW].next(actionType as ForgotPasswordActionType);
            setCurrentView(nextModalView);
            setLoaderStatus(false);
        };

        const onApiFailure = () => {
            setLoaderStatus(false);
        };

        forgotPasswordTypeApiMap[actionType]?.(formValues, onApiSuccess, onApiFailure);
    }, [actionType, formValues, resetPassword, sendLoginLink]);

    const onFormAction = (values: PasswordResetFormValues, type: ForgotPasswordActionType) => {
        setFormValues(values);
        setActionType(type);
        setCustomerSupportView(false);
        setLoaderStatus(true);
    };

    useEffect(() => {
        if (actionType) {
            apiHandlers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.email]);

    const onResendEmail = () => {
        apiHandlers();
        setCustomerSupportView(true);
    };

    const onEmailError = () => {
        setCustomerSupportView(true);
    };

    const generateModalBodyContent = {
        [MAGIC_LINK_FORM_VIEW]: (
            <MagicLinkForm
                onNewPassword={formvalues => onFormAction(formvalues, ForgotPasswordActionType.RESET_PASSWORD)}
                onSubmit={formvalues => onFormAction(formvalues, ForgotPasswordActionType.LOGIN_LINK)}
                onEmailError={onEmailError}
                email={email}
            />
        ),
        [MAGIC_LINK_SENT_VIEW]: (
            <MagicLinkConfirmation
                email={formValues.email}
                onEmailEdit={resetState}
                onResend={onResendEmail}
                expirationTime="15"
                data-testid="mf-forgot-password-magic-link-sent"
            />
        ),
        [RESET_PASSWORD_SENT_VIEW]: (
            <ResetPasswordSent
                email={formValues.email}
                onEmailEdit={resetState}
                onResend={onResendEmail}
                data-testid="mf-forgot-password-reset-password-sent"
            />
        ),
    };
    return (
        <>
            <ForgotPasswordWrapper data-testid="mf-forgot-password-modal">
                <ForgotPasswordLabel
                    type="button"
                    onClick={event => {
                        event.stopPropagation();
                        triggerForgotPasswordOverlay();
                    }}
                >
                    {forgotPasswordLabel}
                </ForgotPasswordLabel>
            </ForgotPasswordWrapper>
            <Modal isOpen={forgotPasswordOverlay} onOpen={noop} onClose={() => resetState(true)} dismissable>
                <StyledModalBody>
                    <Modal.CloseButton />
                    <ContainerViews isLoading={isLoading}>
                        {generateModalBodyContent[currentView]}
                        {customerSupportView && <EmailNotRecieved currentView={currentView} />}
                    </ContainerViews>
                </StyledModalBody>
            </Modal>
        </>
    );
};

export default BellaForgotPassword;
